/**
 * Authentication Actions
 *
 * Handles user authentication, password reset, and retailer token generation.
 */

import {getGrepcatcha} from '@/util';
import {unauthenticatedRequest} from '@/util/request';
import {fetchWithTimeout} from '@/util/request-utils';
import {store} from '@/store';
import {userAtom} from '@/store/user';
import {getUserContext} from '@/actions/user';
import {FP_API_V2_URL} from '@/util/constants/api';
import type {LoginPayload, ResetPasswordPayload} from '@/types/auth';

/**
 * Initiate password reset flow
 * @param email - User's email address
 */
export async function forgotPassword(email: string) {
    return await unauthenticatedRequest(
        '/api/v2/users/forgot_password',
        'POST',
        {email}
    );
}

/**
 * Complete password reset with new password
 * @param payload - Reset payload with password and confirmation
 */
export async function resetPassword(payload: ResetPasswordPayload) {
    const {password, password_confirmation} = payload.user;

    if (password !== password_confirmation) {
        return {
            errors: {user: {password_confirmation: ['Passwords do not match']}},
        };
    }

    try {
        const json = await unauthenticatedRequest(
            '/api/v2/users/forgot_password/complete',
            'PUT',
            payload
        );

        return {user: json, errors: null};
    } catch (error) {
        const message =
            error instanceof Error ? error.message : 'Password reset failed';
        return {user: null, errors: {general: {base: [message]}}};
    }
}

/**
 * Log out current user
 */
export async function logout() {
    return await unauthenticatedRequest(
        '/auth/logout.json',
        'GET',
        null,
        'include'
    );
}

/**
 * Log in user with credentials.
 * Calls FP API directly — no custom auth endpoint needed.
 * @param payload - Login credentials
 * @param onSuccess - Callback on successful login
 */
export async function login(payload: LoginPayload, onSuccess: () => void) {
    const grecaptcha = await getGrepcatcha('login');

    try {
        const response = await fetchWithTimeout(
            `${FP_API_V2_URL}/auth/site_login.json`,
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    user: payload,
                    'g-recaptcha-response': grecaptcha,
                }),
                credentials: 'include',
            }
        );

        if (!response.ok) {
            const data = (await response.json()) as {
                error?: string;
                errors?: unknown;
            };
            const message = data.error ?? `Request failed: ${response.status}`;
            throw new Error(message);
        }

        const json = (await response.json()) as {
            two_factor_login_token?: string;
        };

        if (json.two_factor_login_token) {
            const exp = new Date().getTime() + 15 * 60 * 1000;

            // Store 2FA expiry in sessionStorage for timeout display only —
            // actual 2FA validation happens server-side
            sessionStorage.setItem(
                'modelbank::2fa-timestamp',
                JSON.stringify(exp)
            );

            return {
                user: null,
                errors: null,
                twoFactorToken: json.two_factor_login_token,
            };
        }

        // site_login.json sets session cookies — fetch context using those
        const user = await getUserContext();

        if (!user) throw new Error('Failed to fetch user after login');

        store.set(userAtom, user);
        if (onSuccess) onSuccess();

        return {user, errors: null};
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Login failed';
        return {user: null, errors: {general: {base: [message]}}};
    }
}

/**
 * Verify two-factor authentication code
 * @param token - 2FA token
 * @param otp - One-time password
 */
export async function verifyOneTimePassword(token: string, otp: string) {
    try {
        const response = await fetchWithTimeout(
            `${FP_API_V2_URL}/auth/two_factor_login.json`,
            {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({two_factor_login_token: token, otp}),
                credentials: 'include',
            }
        );

        if (!response.ok) {
            const data = (await response.json()) as {error?: string};
            throw new Error(data.error ?? `Request failed: ${response.status}`);
        }

        // 2FA endpoint sets session cookies — fetch context using those
        const user = await getUserContext();

        if (!user) throw new Error('Failed to fetch user after 2FA');

        store.set(userAtom, user);

        return {user, errors: null};
    } catch (error) {
        const message =
            error instanceof Error ? error.message : 'Verification failed';
        return {user: null, errors: [message]};
    }
}
